import * as React from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Checkbox } from '@/components/ui/checkbox'
import { Button, ButtonText } from '@/components/ui/button'
import useAppStore from '@/store/useAppStore'
import useVaultSelection from '@/pages/dashboard/useVaultSelection'
import useCreateSavedView from '@/services/api/cash-flow/useCreateSavedView'

/** Captures the dashboard's current vault selection, split toggle, horizon and period into a new saved view. */
export default function SaveViewDialog({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState('')
  const [setDefault, setSetDefault] = React.useState(false)

  const { selectedVaultIds, splitByVault } = useVaultSelection()
  const numberOfPeriods = useAppStore((s) => s.numberOfPeriods)
  const periodType = useAppStore((s) => s.periodType)

  const { action: createView, status } = useCreateSavedView()

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return

    await createView({
      surface: 'dashboard',
      name: name.trim(),
      config: {
        vault_ids: selectedVaultIds,
        split_by_vault: splitByVault,
        horizon: numberOfPeriods,
        period: periodType,
      },
      is_default: setDefault,
    })

    setOpen(false)
    setName('')
    setSetDefault(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Save view</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave}>
          <DialogBody className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="saved-view-name">Name</Label>
              <Input
                id="saved-view-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ops focus"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={setDefault}
                onCheckedChange={(checked) => setSetDefault(!!checked)}
              />
              <span className="text-sm text-txt-secondary">
                Set as my default dashboard view
              </span>
            </label>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" loading={status.isPending} disabled={!name.trim()}>
              <ButtonText>Save view</ButtonText>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
